import React from 'react';
import classnames from 'classnames';
import { compact } from 'lodash';

import StrapiLink from '../../StrapiLink/StrapiLink';
import { SchoolInfoBoxFragment } from '../../../../graphql.schema';

type StrapiSchoolInfoBoxPropType = SchoolInfoBoxFragment & {
  hasRTL?: boolean;
};

export const StrapiSchoolInfoBox: React.FC<StrapiSchoolInfoBoxPropType> = ({
  miscLinks,
  socialLinks,
  contactNumber,
  internalLinksListHeading,
  emailAddress,
  externalLinkHeading,
  address,
  internalLinks,
  externalLinksList,
  openingTimes,
  hasRTL,
}) => {
  const isV2 = process.env.GATSBY_IS_V2 === 'true';
  let styles = require('./index.module.scss');
  if (isV2) {
    styles = require('./index-v2.module.scss');

    return (
      <div className={styles.schoolMeta}>
        <div className="container">
          <div className="columns">
            <div className={classnames('column is-6 flex-2')}>
              <div className={classnames('columns is-mobile')}>
                <div className={classnames('column is-5 is-offset-1-tablet')}>
                  <h4 className="title is-4 brand-a">{internalLinksListHeading}</h4>
                  {compact(internalLinks).map((link, idx) => (
                    <StrapiLink
                      className={classnames(styles.strapAlign, styles.isMobileLink)}
                      key={`school-info-box-internal-link-${idx}`}
                      {...link}
                    >
                      {link.title}
                    </StrapiLink>
                  ))}
                </div>
                <div className="column is-5">
                  <h4 className="title is-4 brand-a">{externalLinkHeading}</h4>
                  {compact(externalLinksList).map(({ linkName, linkUrl, openInNewWindow, noFollow }, idx) =>
                    linkName && linkUrl ? (
                      <StrapiLink
                        className={styles.strapAlign}
                        id={`school-info-box-external-link-${idx}`}
                        key={`school-info-box-external-link-${idx}`}
                        url={linkUrl}
                        openInNewWindow={openInNewWindow}
                        nofollow={noFollow}
                        title={linkName ?? ''}
                      />
                    ) : null,
                  )}
                </div>
              </div>
            </div>

            <div className={classnames('column is-5 flex-1 is-offset-1-desktop')}>
              <div className={styles.contactInfo}>
                <div className="mb-4">
                  {compact(socialLinks).map(({ linkName, linkUrl }) => (
                    <a
                      className={classnames('mr-2', styles.links)}
                      key={`${linkName}-${linkUrl}`}
                      href={linkUrl ?? ''}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {linkName}
                    </a>
                  ))}
                </div>
                <p className={classnames('is-text-2 black-2 mb-4', styles.wrapText)}>{openingTimes}</p>
                <p className={classnames('is-text-2 black-2 mb-4', styles.wrapText)}>{address}</p>
                <div className="mt-3">
                  <a className="is-block is-text-2" href={`tel:${contactNumber}`}>
                    {contactNumber}
                  </a>
                  <a className="is-block is-text-2 mb-4" href={`mailto:${emailAddress}`}>
                    {emailAddress}
                  </a>
                  {compact(miscLinks).map(({ linkName, linkUrl }) => (
                    <a
                      className="mr-2"
                      key={`${linkName}-${linkUrl}`}
                      href={linkUrl ?? ''}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {linkName}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.schoolMeta}>
      <div className="columns">
        <div className={classnames('column is-6 flex-2')}>
          <div className={classnames('columns is-mobile')}>
            <div className={classnames('column is-5 is-offset-1-tablet')}>
              <h4 className="title is-4 brand-a">{internalLinksListHeading}</h4>
              {compact(internalLinks).map((link, idx) => (
                <StrapiLink
                  className={classnames(styles.strapAlign, styles.isMobileLink)}
                  key={`school-info-box-internal-link-${idx}`}
                  {...link}
                >
                  {link.title}
                </StrapiLink>
              ))}
            </div>
            <div className="column is-5">
              <h4 className="title is-4 brand-a">{externalLinkHeading}</h4>
              {compact(externalLinksList).map(({ linkName, linkUrl, openInNewWindow, noFollow }, idx) =>
                linkName && linkUrl ? (
                  <StrapiLink
                    className={styles.strapAlign}
                    id={`school-info-box-external-link-${idx}`}
                    key={`school-info-box-external-link-${idx}`}
                    url={linkUrl}
                    openInNewWindow={openInNewWindow}
                    nofollow={noFollow}
                    title={linkName ?? ''}
                  />
                ) : null,
              )}
            </div>
          </div>
        </div>

        <div className={classnames('column is-5 flex-1 is-offset-1-desktop')}>
          <div className={styles.contactInfo}>
            <div className="mb-4">
              {compact(socialLinks).map(({ linkName, linkUrl }) => (
                <a
                  className={classnames('mr-2', styles.links)}
                  key={`${linkName}-${linkUrl}`}
                  href={linkUrl ?? ''}
                  rel="noreferrer"
                  target="_blank"
                >
                  {linkName}
                </a>
              ))}
            </div>
            <p className={classnames('is-text-2 black-2 mb-4', styles.wrapText)}>{openingTimes}</p>
            <p className={classnames('is-text-2 black-2 mb-4', styles.wrapText)}>{address}</p>
            <div className="mt-3">
              <a className="is-block is-text-2" href={`tel:${contactNumber}`}>
                {contactNumber}
              </a>
              <a className="is-block is-text-2 mb-4" href={`mailto:${emailAddress}`}>
                {emailAddress}
              </a>
              {compact(miscLinks).map(({ linkName, linkUrl }) => (
                <a
                  className="mr-2"
                  key={`${linkName}-${linkUrl}`}
                  href={linkUrl ?? ''}
                  rel="noreferrer"
                  target="_blank"
                >
                  {linkName}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
